import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RiFileList3Line, RiFileAddLine, RiFolderSettingsLine } from "react-icons/ri";
import {
  MdOutlineKeyboardArrowRight,
  MdDashboard,
  MdPerson,
  MdOutlineFolder,
  MdOutlinePeopleAlt,
  MdOutlineDomain,
  MdCloudDone,
  MdListAlt,
} from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";
import { IoPersonAddOutline } from "react-icons/io5";
import useAuth from "../useAuth";

export default function NavBar() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="w-full min-h-screen bg-white text-gray-700 flex flex-col p-4 border-r border-gray-100 font-sans">
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 mb-8 pt-2">
        <img src="/assets/logo.png" alt="logo" className="w-10 h-10" />
        <span className="text-xl font-medium text-gray-600">ProjectMgr</span>
      </div>

      {/* Main Routes */}
      <div className="space-y-1">
        <Route
          name="Dashboard"
          icon={<MdDashboard className="text-xl" />}
          onClick={() => navigate("/my/home")}
          urlPath="home"
        />

        {/* Sections */}
        <Section title="Projects">
          <Route
            name="Register Project"
            icon={<MdOutlineFolder className="text-xl" />}
            onClick={() => navigate("/my/registerProject")}
            urlPath="registerProject"
          />
          <Route
            name="Projects List"
            icon={<MdOutlineFolder className="text-xl" />}
            onClick={() => navigate("/my/projectsList")}
            urlPath="projectsList"
          />
        </Section>

        {/* Sections */}
        {user.userType === "admin" && (
          <Section title="Domains">
            <Route
              name="Register Domain"
              icon={<MdOutlineDomain className="text-xl" />}
              onClick={() => navigate("/my/registerDomain")}
              urlPath="registerDomain"
            />
            <Route
              name="Domains List"
              icon={<MdOutlineDomain className="text-xl" />}
              onClick={() => navigate("/my/domainsList")}
              urlPath="domainsList"
            />
          </Section>
        )}

        {/* Documentations */}
        <Section title="Documentations">
          <Route
            name="Add New Doc"
            icon={<RiFileAddLine className="text-xl" />}
            onClick={() => navigate("/my/addNewDoc")}
            urlPath="addNewDoc"
          />
          <Route
            name="Documents"
            icon={<RiFileList3Line className="text-xl" />}
            onClick={() => navigate("/my/docsList")}
            urlPath="docsList"
          />
          <Route
            name="Categories"
            icon={<RiFolderSettingsLine className="text-xl" />}
            onClick={() => navigate("/my/categoryManager")}
            urlPath="categoryManager"
          />
        </Section>

        {/* Employees */}
        <Section title="Employees">
          <Route
            name="Register Employee"
            icon={<MdOutlinePeopleAlt className="text-xl" />}
            onClick={() => navigate("/my/registerEmployee")}
            urlPath="registerProject"
            disabled
          />
          <Route
            name="Employees List"
            icon={<MdOutlinePeopleAlt className="text-xl" />}
            onClick={() => navigate("/my/employeesList")}
            urlPath="projectsList"
            disabled
          />
        </Section>

        {/* Users Management */}
        {user?.userType === "admin" && (
          <Section title="Users Management">
            <Route
              name="Users List"
              icon={<MdOutlinePeopleAlt className="text-xl" />}
              onClick={() => navigate("/my/usersList")}
              urlPath="usersList"
            />
            <Route
              name="Register User"
              icon={<IoPersonAddOutline className="text-xl" />}
              onClick={() => navigate("/my/registerUser")}
              urlPath="registerUser"
            />
          </Section>
        )}

        {/* Backups & Security */}
        <Section title="Backups & Security">
          <Route
            name="Backup Dashboard"
            icon={<MdCloudDone className="text-xl text-blue-600" />}
            onClick={() => navigate("/my/backupDashboard")}
            urlPath="backupDashboard"
          />
          <Route
            name="Backup Logs"
            icon={<MdListAlt className="text-xl text-indigo-600" />}
            onClick={() => navigate("/my/backupLogsList")}
            urlPath="backupLogsList"
          />
        </Section>

        <Section title="User Profile">
          <Route
            name="Profile"
            icon={<MdPerson className="text-xl" />}
            onClick={() => navigate("/my/profile")}
            urlPath="profile"
          />
        </Section>
      </div>
    </div>
  );
}

// Section with Framer Motion animation
const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);

  return (
    <div className="mt-4">
      <div
        className="flex items-center justify-between cursor-pointer px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors group"
        onClick={() => setOpen(!open)}
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 group-hover:text-gray-700">
          {title}
        </span>
        <MdOutlineKeyboardArrowRight
          className={`transition-transform duration-300 text-gray-400 group-hover:text-gray-600 ${
            open ? "rotate-90" : "rotate-0"
          }`}
        />
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden mt-1"
          >
            <div className="flex flex-col">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Individual route button
const Route = ({ name, icon, onClick, urlPath, disabled = false }) => {
  const location = useLocation();
  const active = location.pathname.includes(urlPath);

  return (
    <div
      onClick={() => {
        if (!disabled && onClick) onClick();
      }}
      className={`relative flex items-center gap-4 px-4 py-3 text-sm font-medium rounded-r-full mr-2 transition-all duration-200
        ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
        ${
          active && !disabled
            ? "bg-blue-100 text-blue-800"
            : "text-gray-600 hover:bg-gray-100"
        }
      `}
    >
      <div className={`${active ? "text-blue-800" : "text-gray-500"}`}>
        {icon}
      </div>
      <span className="truncate">{name}</span>
      {active && !disabled && (
        <motion.div
          layoutId="active-pill"
          className="absolute left-0 top-0 bottom-0 w-1 bg-blue-600 rounded-r-full"
        />
      )}
    </div>
  );
};
